import { SWW } from "@/config/toast";
import { submitLeadForm } from "@/helpers/api/service";
import { AuthContext } from "@/providers/auth";
import { useRouter } from "next/router";
import { ChangeEvent, useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import FaPhone from "../SVG/Fontawesome/FaPhone";
import LinkWrapper from "../LinkWrapper";
import { reverseCourseNavigationData } from "@/config/navigation";
import dynamic from "next/dynamic"
import { useDispatch } from "react-redux";
import { updateOffCanvasView } from "@/store/slices/offCanvas";

const LeadCallReasons = dynamic(() => import("./LeadCallReasons"));
const Student = dynamic(() => import("../SVG/Student"))
const Parents = dynamic(() => import("../SVG/Parents"));

const CourseLeadForm = (props: {
  title?: string;
  className?: string;
}) => {
  const router = useRouter();
  const authContext = useContext(AuthContext);

  const [isLoggedIn, updateIsLoggedIn] = useState<boolean>(false);
  const [mobileNumberInput, updateMobileNumberInput] = useState<string>("")
  const [selectedPhoneNumberOwner, updateselectedPhoneNumberOwner] = useState<string>("");
  const [selectedCallReason, updateselectedCallReason] = useState<string>("");
  const [selectedStudentClass, updateSelectedStudentClass] = useState<string>("");
  const [selectedCategory, updateSelectedCategory] = useState<string>("");
  const [categoryOptions, updateCategoryOptions] = useState<{ category: string }[]>([]);
  const [callingReasonOptions, updateCallingReasonOptions] = useState<{ text: string; icon: string; value: string; }[]>([]);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [apiCall, setApiCall] = useState<boolean>(false);
  const formRef = useRef<HTMLFormElement>(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!selectedStudentClass || !selectedCategory) return;

    const examCategory = String(router.query.Exam || "").split("_")?.[0];
    const category = examCategory ? examCategory : selectedCategory;

    const callReasonOptions = [
      { text: `Mujhe ${category} Online course khareedna hai. Mujhe admission mein help ke liye call karein.`, icon: "Learner", value: "100" },
      { text: `Mujhe ${category} Course ke baare mein aur jaankari chahiye.`, icon: "Info", value: "80" },
      { text: `Mujhe ek Counsellor/Teacher se connect karna hai.`, icon: "Counseller", value: "40" },
      { text: `Main ek Paid Course ka student hoon - mujhe web par courses online nahin mil rahe.`, icon: "PaidStudent", value: "20" },
      { text: `Main web par course mein login nahi kar paa raha hoon. Kripya madad karein.`, icon: "QuestionMark", value: "0" }
    ];
    if (category !== selectedCategory) {
      // user selected different category compared to current category page 
      callReasonOptions.splice(2, 0, { text: `Main ${selectedStudentClass === "13" ? "Dropper" : `${selectedStudentClass === "14" ? "" : `Class ${selectedStudentClass}`}`} ${selectedCategory} Course mein interested hoon. Mujhe admission mein help ke liye call karein.`, icon: "EducationBook", value: "60" })
    }
    // random sort
    callReasonOptions.sort((_) => Math.random() - 0.5)
    updateCallingReasonOptions(callReasonOptions);
  }, [selectedStudentClass, selectedCategory, router.query])

  useEffect(() => {
    if (!selectedStudentClass) return;
    updateCategoryOptions(reverseCourseNavigationData[selectedStudentClass]);
    updateSelectedCategory("");
  }, [selectedStudentClass])

  useEffect(() => {
    if (!authContext.contextResolved) return;

    updateIsLoggedIn(!!authContext.user?.id)
  }, [authContext.contextResolved, authContext.user])

  const submitForm = (e: any, secondaryCTA?: boolean) => {
    if (!secondaryCTA) e.preventDefault();
    const formData = new FormData(formRef.current as any);

    const formResponseObj: any = Object.fromEntries(formData);
    formResponseObj.source = router.asPath;
    formResponseObj.studentId = authContext.studentId.toString();
    formResponseObj.verifiedStudentId = authContext.user?.id;
    setApiCall(true);
    submitLeadForm(formResponseObj)
      .then((res: any) => {
        if (!secondaryCTA) {
          setSubmitted(true);
        }
      })
      .catch(() => {
        toast.error(SWW);
      })
      .then(() => {
        setApiCall(false);
      });
  };

  const mobileValidator = (e: ChangeEvent<HTMLInputElement>) => {
    updateMobileNumberInput(e.target.value.replace(/[^0-9]/g, "").substring(0, 10));
  };

  if (submitted) {
    const examCategory = String(router.query.Exam || "").split("_")?.[0];
    const category = (!examCategory || Number(selectedCallReason) === 60) ? selectedCategory : examCategory;

    return (
      <div style={{ background: "linear-gradient(rgba(255,241,221,0.95), rgba(255,241,221,0.9)), url('https://static.doubtnut.com/engagement_framework/leadformbackground.webp')" }} className="relative card mx-auto w-full sm:max-w-full lg:max-w-sm flex flex-col self-baseline items-center py-20 gap-2 !bg-cover !bg-no-repeat">

        <p className="text-center font-semibold">Thanks for submitting your response.</p>

        {
          Number(selectedCallReason) >= 60 && <>
            <div className="mt-4 text-center font-medium">
              Want to buy
              <strong className="block text-2xl">{category} Course?</strong>
            </div>

            <LinkWrapper href="tel:+911247158250" className="no-underline mt-2 hover-pop">
              <FaPhone className="w-10 !h-10  bg-green-700 p-6 rounded-full text-white" />
              <strong className="block text-center">Call Now</strong>
            </LinkWrapper>
          </>
        }

        {
          Number(selectedCallReason) < 60 && <>
            <div className="mt-4 text-center">
              <strong className="block text-2xl font-semibold">We heard you.</strong>
            </div>

            <div className="mt-2 text-center font-medium">
              We&apos;ll get back to you soon. For detailed information, please mail us at <LinkWrapper href="mailto:info@doubtnut.com">info@doubtnut.com</LinkWrapper>
            </div>
          </>
        }
      </div>
    );
  }

  return (
    <div className={`card w-full max-w-sm sm:w-96 mx-auto flex flex-col self-baseline gap-4 bg-secondary ${props.className ?? ""}`}>
      <h5 className="text-lg font-semibold mb-2">
        {props.title || "Want to Talk To Us?"}
      </h5>
      <form ref={formRef} className="w-full max-w-sm flex flex-col justify-center gap-4" onSubmit={submitForm}>
        <input
          required
          name="leadName"
          type="text"
          placeholder="Enter Student’s Name"
          className="input"
        />
        <input
          required
          name="leadMobile"
          type="tel"
          placeholder="Enter Mobile Number"
          className="input"
          onChange={mobileValidator}
          value={mobileNumberInput}
          pattern="[6-9][0-9]{9}"
        />

        {
          mobileNumberInput.length === 10 && <>
            <label className="font-semibold text-sm">
              Who&apos;s phone number it is
            </label>
            <div className="flex gap-4">
              <label className={`flex-1 flex flex-col gap-4 items-center text-center cursor-pointer card !rounded-xl aspect-square border hover:border-green-700 ${selectedPhoneNumberOwner === "self" ? "bg-green-50 border border-green-700" : ""}`} htmlFor="lead-owner-self">
                <input type="radio" id="lead-owner-self" name="leadMobileOwner" value="self" className="accent-green-700 cursor-pointer self-start" checked={selectedPhoneNumberOwner === "self"} onChange={(e) => updateselectedPhoneNumberOwner(e.target.value)} required />
                <Student className="w-14 !h-14" />
                <span className="text-sm">My Phone Number (Student)</span>
              </label>

              <label className={`flex-1 flex flex-col gap-4 items-center text-center cursor-pointer card !rounded-xl aspect-square border hover:border-green-700 ${selectedPhoneNumberOwner === "others" ? "bg-green-50 border border-green-700" : ""}`} htmlFor="lead-owner-others">
                <input type="radio" id="lead-owner-others" name="leadMobileOwner" value="others" className="accent-green-700 cursor-pointer self-start" checked={selectedPhoneNumberOwner === "others"} onChange={(e) => updateselectedPhoneNumberOwner(e.target.value)} required />
                <Parents className="w-14 !h-14" />
                <span className="text-sm">Parents / Brother / Sister / Friend</span>
              </label>
            </div>
          </>
        }

        <label className="font-semibold text-sm">
          Class and Board / Exam Information
        </label>

        <select
          required
          name="leadStudentClass"
          className="input cursor-pointer"
          value={selectedStudentClass}
          onChange={(e) => updateSelectedStudentClass(e.target.value)}
        >
          <option value="" disabled>
            Select Student&apos;s Class
          </option>
          <option value="6">Class 6</option>
          <option value="7">Class 7</option>
          <option value="8">Class 8</option>
          <option value="9">Class 9</option>
          <option value="10">Class 10</option>
          <option value="11">Class 11</option>
          <option value="12">Class 12</option>
          <option value="13">Dropper</option>
          <option value="14">Govt. Exams</option>
        </select>

        <select
          required
          name="leadBoard"
          value={selectedCategory}
          onChange={(e) => updateSelectedCategory(e.target.value)}
          className="input cursor-pointer"
        >
          <option value="" disabled>
            Select Student&apos;s School Board / Exam
          </option>
          {!!categoryOptions?.length && categoryOptions.map((item, index) => {
            return <option key={index.toString()} value={item.category}>{item.category}</option>
          })}
        </select>

        {
          (selectedCategory && selectedStudentClass) && <LeadCallReasons items={callingReasonOptions} selectedCallReason={selectedCallReason} updateselectedCallReason={updateselectedCallReason} />
        }

        <div className="flex flex-col mt-2 gap-4 max-w-sm w-full">
          <button type="submit" className="btn-primary">
            Submit
          </button>

          {
            !isLoggedIn &&
            <span className="self-center text-sm">
              Do you want to login?
              <button className="inline-block text-primary font-semibold py-0 ml-2 no-underline text-center" onClick={() => dispatch(updateOffCanvasView({ name: "LOGIN", data: { LOGIN: { method: "OTP" } } }))}>
                Login Here
              </button>
            </span>
          }
        </div>
      </form>
    </div>
  );
};

export default CourseLeadForm;
